import { useEffect, useState } from "react";
import { Download, Loader2, CheckCircle2 } from "lucide-react";
import { useLeadGuard } from "@/components/LeadGuard";

type Props = {
  /** PDF (or other file) the email unlocks. */
  fileUrl: string;
  /** What we promise the visitor. */
  promise?: string;
  /** Compact variant for inline use (e.g. footer). */
  compact?: boolean;
  /** Optional ID to differentiate signups (e.g. "aeo-checklist"). */
  source?: string;
};

const STORAGE_KEY = "ft_newsletter_email";

export function NewsletterGate({ fileUrl, promise, compact, source }: Props) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [errMsg, setErrMsg] = useState("");
  const { guardFields, honeypot } = useLeadGuard();

  // Read after mount: reading localStorage during render makes the server and
  // client markup disagree and React throws away the hydrated tree.
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) setState("ok");
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setState("err");
      setErrMsg("Enter a valid email address.");
      return;
    }
    setState("loading");
    try {
      const res = await fetch("/api/public/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: v, source: source ?? "newsletter", ...guardFields() }),
      });
      if (!res.ok) throw new Error(String(res.status));
      // Only remember the visitor once the signup actually went through.
      localStorage.setItem(STORAGE_KEY, v);
      localStorage.setItem(`${STORAGE_KEY}_source`, source ?? "unknown");
      setState("ok");
      // Auto-trigger download if a file is attached.
      if (fileUrl) {
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = "";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch {
      setState("err");
      setErrMsg("Couldn't subscribe just now. Please try again.");
    }
  };

  if (state === "ok") {
    return (
      <div className={`rounded-2xl border border-primary/30 bg-primary/5 ${compact ? "p-4" : "p-6"} flex items-center gap-3`}>
        <CheckCircle2 className="text-primary flex-none" size={18} />
        <div className="text-sm">
          <div className="font-medium">You're on the list.</div>
          {fileUrl && (
            <a href={fileUrl} className="text-primary underline underline-offset-4">
              Download didn't start? Click here.
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={compact ? "relative flex flex-col sm:flex-row gap-2" : "relative flex flex-col sm:flex-row gap-3"}>
      {honeypot}
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        aria-label="Email address"
        autoComplete="email"
        className={`min-w-0 flex-1 rounded-full border bg-card px-5 ${compact ? "py-3 text-sm" : "py-4 text-sm"} focus:outline-none focus:border-primary/60 ${
          state === "err" ? "border-destructive" : "border-border"
        }`}
      />
      <button
        type="submit"
        disabled={state === "loading"}
        className={`inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground ${compact ? "px-5 py-3" : "px-7 py-4"} text-sm font-medium hover:shadow-[var(--shadow-glow)] disabled:opacity-60`}
      >
        {state === "loading" ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Subscribing</>
        ) : fileUrl ? (
          <><Download size={16} /> {promise ?? "Get the PDF"}</>
        ) : (
          <>{promise ?? "Subscribe"}</>
        )}
      </button>
      {state === "err" && errMsg && (
        <p className="text-xs text-destructive sm:basis-full">{errMsg}</p>
      )}
    </form>
  );
}